import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard,
  ShoppingCart,
  Users,
  Crown,
  Loader2,
  Search,
  Package,
  DollarSign,
  TrendingUp,
  Calendar,
  Truck,
  Mail,
  Phone,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "orders", label: "Orders", icon: ShoppingCart },
  { id: "customers", label: "Customers", icon: Users },
  { id: "memberships", label: "Memberships", icon: Crown },
];

function currency(n) {
  return `$${Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function StatCard({ label, value, icon: Icon, sub }) {
  return (
    <div className="bg-brand-parchment rounded-2xl p-6 border border-brand-moss/10">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.3em] text-brand-moss/60">
          {label}
        </span>
        {Icon && (
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-brand-moss/10 text-brand-moss">
            <Icon className="w-4 h-4" strokeWidth={1.8} />
          </span>
        )}
      </div>
      <div className="mt-3 font-serif text-3xl text-brand-moss leading-none">{value}</div>
      {sub && <div className="mt-2 text-xs text-brand-ink/60">{sub}</div>}
    </div>
  );
}

function Overview({ stats }) {
  if (!stats) {
    return (
      <div className="flex items-center gap-2 text-brand-moss/60">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading stats…
      </div>
    );
  }
  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Revenue This Month"
          value={currency(stats.revenue_month)}
          icon={DollarSign}
          sub={`${stats.orders_month} paid order${stats.orders_month === 1 ? "" : "s"}`}
        />
        <StatCard
          label="Orders Today"
          value={stats.orders_today}
          icon={ShoppingCart}
          sub={`${stats.pending_orders} pending checkouts`}
        />
        <StatCard
          label="Active Members"
          value={stats.active_memberships}
          icon={Crown}
          sub={`MRR ${currency(stats.mrr)}`}
        />
        <StatCard
          label="Customers"
          value={stats.total_customers}
          icon={Users}
          sub={`${stats.signups_month} joined this month`}
        />
      </div>

      <div className="bg-brand-parchment rounded-2xl p-6 border border-brand-moss/10">
        <div className="flex items-center gap-2 text-brand-moss/70 mb-3">
          <TrendingUp className="w-4 h-4" />
          <span className="text-xs uppercase tracking-[0.3em]">Quick reads</span>
        </div>
        <ul className="space-y-2 text-sm text-brand-ink/80">
          <li>
            You&apos;ve made <strong className="text-brand-moss">{currency(stats.revenue_month)}</strong>{" "}
            in {stats.orders_month} paid order{stats.orders_month === 1 ? "" : "s"} this month.
          </li>
          <li>
            <strong className="text-brand-moss">{stats.pending_orders}</strong> checkouts started but
            not yet paid — these are abandoned carts worth following up on.
          </li>
          <li>
            <strong className="text-brand-moss">{stats.active_memberships}</strong> active members
            paying{" "}
            <strong className="text-brand-moss">{currency(stats.mrr)}</strong>/mo recurring.
          </li>
        </ul>
      </div>
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState(null);
  const [statusFilter, setStatusFilter] = useState("paid");
  const [search, setSearch] = useState("");

  useEffect(() => {
    api
      .get(`/admin/orders`, { params: statusFilter ? { status: statusFilter } : {} })
      .then((r) => setOrders(r.data.orders))
      .catch(() => toast.error("Couldn't load orders."));
  }, [statusFilter]);

  const filtered = (orders || []).filter((o) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (o.customer_email || "").toLowerCase().includes(s) ||
      (o.customer_name || "").toLowerCase().includes(s) ||
      (o.session_id || "").toLowerCase().includes(s)
    );
  });

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex gap-1 bg-brand-parchment border border-brand-moss/10 rounded-full p-1">
          {[
            { v: "paid", label: "Paid" },
            { v: "initiated", label: "Pending" },
            { v: "", label: "All" },
          ].map((f) => (
            <button
              key={f.v || "all"}
              data-testid={`filter-${f.v || "all"}`}
              onClick={() => setStatusFilter(f.v)}
              className={`px-4 py-1.5 text-xs uppercase tracking-[0.25em] rounded-full transition-colors ${
                statusFilter === f.v
                  ? "bg-brand-moss text-brand-gold"
                  : "text-brand-moss/70 hover:text-brand-moss"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 text-brand-moss/40 absolute top-1/2 left-3 -translate-y-1/2" />
          <input
            data-testid="orders-search"
            type="text"
            placeholder="Search by email, name, session…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-brand-parchment border border-brand-moss/15 rounded-full pl-10 pr-4 py-2 text-sm focus:outline-none focus:border-brand-gold"
          />
        </div>
      </div>

      {orders === null ? (
        <div className="flex items-center gap-2 text-brand-moss/60">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading orders…
        </div>
      ) : filtered.length === 0 ? (
        <p data-testid="orders-empty" className="text-brand-moss/60 text-sm">
          No orders match this filter.
        </p>
      ) : (
        <div className="space-y-3">
          {filtered.map((o) => (
            <details
              key={o.id || o.session_id}
              data-testid={`order-${o.session_id}`}
              className="group bg-brand-parchment border border-brand-moss/10 rounded-2xl overflow-hidden"
            >
              <summary className="cursor-pointer list-none px-6 py-4 flex flex-wrap items-center gap-4 hover:bg-white/40 transition-colors">
                <span
                  className={`shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.25em] px-3 py-1 rounded-full ${
                    o.payment_status === "paid"
                      ? "bg-brand-moss/15 text-brand-moss"
                      : o.payment_status === "initiated"
                      ? "bg-[#F0E9D6] text-[#B7574E]"
                      : "bg-brand-ink/10 text-brand-ink/70"
                  }`}
                >
                  {o.payment_status || "unknown"}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-lg text-brand-moss leading-tight truncate">
                    {o.customer_name || o.customer_email || "Guest checkout"}
                  </p>
                  <p className="text-xs text-brand-moss/60 truncate">
                    {o.customer_email} · {o.kind === "membership" ? "Membership" : "Products"} ·{" "}
                    {fmtDate(o.created_at)}
                  </p>
                </div>
                <span className="font-serif text-xl text-brand-moss shrink-0">
                  {currency(o.amount)}
                </span>
              </summary>

              <div className="px-6 pb-5 pt-1 border-t border-brand-moss/10 grid md:grid-cols-2 gap-6 text-sm">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-brand-moss/60 mb-2">
                    Items
                  </p>
                  {(o.items || []).map((it, i) => (
                    <p key={i} className="text-brand-ink/80">
                      {it.quantity}× {it.name} — {currency(it.line_total)}
                    </p>
                  ))}
                  <p className="mt-2 text-[10px] uppercase tracking-[0.3em] text-brand-moss/60">
                    Session
                  </p>
                  <p className="text-brand-ink/60 text-xs break-all">{o.session_id}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-brand-moss/60 mb-2">
                    Shipping
                  </p>
                  {o.shipping ? (
                    <div className="text-brand-ink/80 space-y-0.5">
                      <p className="inline-flex items-center gap-1.5">
                        <Truck className="w-3 h-3 text-brand-moss/50" />
                        {o.shipping.name}
                      </p>
                      <p>{o.shipping.line1}</p>
                      {o.shipping.line2 && <p>{o.shipping.line2}</p>}
                      <p>
                        {o.shipping.city}, {o.shipping.state} {o.shipping.postal_code}
                      </p>
                      <p>{o.shipping.country}</p>
                    </div>
                  ) : (
                    <p className="text-brand-moss/50 italic">
                      {o.kind === "membership"
                        ? "Membership — no shipping needed"
                        : "Not collected yet"}
                    </p>
                  )}
                  {o.customer_phone && (
                    <p className="mt-2 inline-flex items-center gap-1.5 text-brand-ink/80">
                      <Phone className="w-3 h-3 text-brand-moss/50" />
                      {o.customer_phone}
                    </p>
                  )}
                </div>
              </div>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}

function CustomersTab() {
  const [customers, setCustomers] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api
      .get(`/admin/customers`)
      .then((r) => setCustomers(r.data.customers))
      .catch(() => toast.error("Couldn't load customers."));
  }, []);

  const filtered = (customers || []).filter((c) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (c.email || "").toLowerCase().includes(s) ||
      (c.full_name || "").toLowerCase().includes(s) ||
      (c.name || "").toLowerCase().includes(s)
    );
  });

  return (
    <div>
      <div className="relative max-w-md mb-5">
        <Search className="w-4 h-4 text-brand-moss/40 absolute top-1/2 left-3 -translate-y-1/2" />
        <input
          data-testid="customers-search"
          type="text"
          placeholder="Search by name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full bg-brand-parchment border border-brand-moss/15 rounded-full pl-10 pr-4 py-2 text-sm focus:outline-none focus:border-brand-gold"
        />
      </div>

      {customers === null ? (
        <div className="flex items-center gap-2 text-brand-moss/60">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading customers…
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-brand-moss/60 text-sm">No customers yet.</p>
      ) : (
        <div className="overflow-x-auto bg-brand-parchment border border-brand-moss/10 rounded-2xl">
          <table className="w-full text-sm">
            <thead className="border-b border-brand-moss/10 text-[10px] uppercase tracking-[0.25em] text-brand-moss/60">
              <tr>
                <th className="text-left px-4 py-3">Name / Email</th>
                <th className="text-left px-4 py-3">Joined</th>
                <th className="text-right px-4 py-3">Orders</th>
                <th className="text-right px-4 py-3">Spend</th>
                <th className="text-left px-4 py-3">Membership</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr
                  key={c.user_id}
                  data-testid={`customer-${c.user_id}`}
                  className="border-t border-brand-moss/5 hover:bg-white/40 transition-colors"
                >
                  <td className="px-4 py-3">
                    <p className="font-serif text-brand-moss">
                      {c.full_name || c.name || c.email}
                    </p>
                    <p className="text-xs text-brand-moss/60 inline-flex items-center gap-1">
                      <Mail className="w-3 h-3" /> {c.email}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-brand-ink/70">{fmtDate(c.created_at)}</td>
                  <td className="px-4 py-3 text-right font-mono text-brand-moss">
                    {c.orders_count}
                  </td>
                  <td className="px-4 py-3 text-right font-serif text-brand-moss">
                    {currency(c.total_spend)}
                  </td>
                  <td className="px-4 py-3">
                    {c.membership_status ? (
                      <span
                        className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.25em] px-2.5 py-0.5 rounded-full ${
                          c.membership_status === "active"
                            ? "bg-brand-moss/15 text-brand-moss"
                            : "bg-[#F0E9D6] text-[#B7574E]"
                        }`}
                      >
                        {c.membership_status}
                      </span>
                    ) : (
                      <span className="text-xs text-brand-moss/40">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MembershipsTab() {
  const [memberships, setMemberships] = useState(null);

  useEffect(() => {
    api
      .get(`/admin/memberships`)
      .then((r) => setMemberships(r.data.memberships))
      .catch(() => toast.error("Couldn't load memberships."));
  }, []);

  if (memberships === null) {
    return (
      <div className="flex items-center gap-2 text-brand-moss/60">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading memberships…
      </div>
    );
  }
  if (memberships.length === 0) {
    return <p className="text-brand-moss/60 text-sm">No memberships yet.</p>;
  }

  const active = memberships.filter((m) => m.status === "active").length;
  const paused = memberships.filter((m) => m.status === "paused").length;
  const cancelled = memberships.filter(
    (m) => m.status === "cancelled" || m.status === "canceled"
  ).length;

  return (
    <div>
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <StatCard label="Active" value={active} icon={Crown} sub={`MRR ${currency(active * 10)}`} />
        <StatCard label="Paused" value={paused} icon={Calendar} />
        <StatCard label="Cancelled" value={cancelled} icon={Package} />
      </div>

      <div className="overflow-x-auto bg-brand-parchment border border-brand-moss/10 rounded-2xl">
        <table className="w-full text-sm">
          <thead className="border-b border-brand-moss/10 text-[10px] uppercase tracking-[0.25em] text-brand-moss/60">
            <tr>
              <th className="text-left px-4 py-3">Member</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Current Scent</th>
              <th className="text-left px-4 py-3">Next Billing</th>
            </tr>
          </thead>
          <tbody>
            {memberships.map((m) => (
              <tr
                key={m.user_id + (m.created_at || "")}
                data-testid={`membership-${m.user_id}`}
                className="border-t border-brand-moss/5 hover:bg-white/40 transition-colors"
              >
                <td className="px-4 py-3">
                  <p className="font-serif text-brand-moss">
                    {m.customer_name || m.customer_email}
                  </p>
                  <p className="text-xs text-brand-moss/60">{m.customer_email}</p>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.25em] px-2.5 py-0.5 rounded-full ${
                      m.status === "active"
                        ? "bg-brand-moss/15 text-brand-moss"
                        : m.status === "paused"
                        ? "bg-[#F0E9D6] text-[#B7574E]"
                        : "bg-brand-ink/10 text-brand-ink/70"
                    }`}
                  >
                    {m.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-brand-ink/80">{m.current_scent || "—"}</td>
                <td className="px-4 py-3 text-brand-ink/70">{fmtDate(m.next_billing_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (user && user.role === "admin") {
      api
        .get(`/admin/stats`)
        .then((r) => setStats(r.data))
        .catch(() => toast.error("Couldn't load stats."));
    }
  }, [user]);

  if (loading) {
    return (
      <section className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-brand-moss" />
      </section>
    );
  }
  if (!user) return <Navigate to="/login?redirect=/admin" replace />;
  if (user.role !== "admin") {
    return (
      <section data-testid="admin-forbidden" className="min-h-[60vh] flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <h1 className="font-serif text-3xl text-brand-moss">Admin only</h1>
          <p className="mt-3 text-brand-moss/70">
            This area is for the 414, LLC founder. If that&apos;s you, sign in with your admin account.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="admin-page" className="bg-brand-cream min-h-screen">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 lg:py-16">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <span className="text-xs uppercase tracking-[0.35em] text-brand-gold">
              414 LLC · Command Room
            </span>
            <h1 className="mt-2 font-serif text-4xl lg:text-5xl text-brand-moss leading-tight">
              Welcome back, <span className="italic">{user.full_name || user.name || "Founder"}.</span>
            </h1>
          </div>
        </div>

        {/* Tabs */}
        <div
          data-testid="admin-tabs"
          className="flex flex-wrap gap-1 bg-brand-parchment border border-brand-moss/10 rounded-full p-1 w-fit mb-8"
        >
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs uppercase tracking-[0.25em] transition-colors ${
                  tab === t.id
                    ? "bg-brand-moss text-brand-gold"
                    : "text-brand-moss/70 hover:text-brand-moss"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>

        <div data-testid={`admin-tab-content-${tab}`}>
          {tab === "overview" && <Overview stats={stats} />}
          {tab === "orders" && <OrdersTab />}
          {tab === "customers" && <CustomersTab />}
          {tab === "memberships" && <MembershipsTab />}
        </div>
      </div>
    </section>
  );
}
